import * as d3 from 'd3';
import { Prediction } from '../../../types/predictions';
import { Theme } from '../../../theme';
import { formatPrice } from '../../../utils/priceUtils';

interface RenderProps {
  chartArea: d3.Selection<SVGGElement, unknown, null, undefined>;
  predictions: Prediction[];
  activePredictions: number[];
  xScale: d3.ScaleTime<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  theme: Theme;
  chartData?: Array<{ time: Date; value: number }>;
}

// Helper function to find crossing point for a prediction
const findCrossingPoint = (
  chartData: Array<{ time: Date; value: number }> = [], 
  prediction: Prediction
): { time: Date; value: number } | null => {
  if (!chartData.length || !prediction.price_min) return null;

  // Sort data by time to ensure chronological order
  const sortedData = [...chartData].sort((a, b) => 
    a.time.getTime() - b.time.getTime()
  );
  
  // Create date objects if needed - Fix instanceof check
  const startDate = typeof prediction.start_date === 'string' ? 
    new Date(prediction.start_date) : prediction.start_date;

  const targetPrice = Number(prediction.price_min);
  
  // Find the first data point where price crosses the prediction threshold
  // Only consider data points after the prediction start date
  for (let i = 0; i < sortedData.length - 1; i++) {
    const curr = sortedData[i];
    const next = sortedData[i + 1];
    
    // Skip if the current point is before the prediction start date
    if (curr.time < (startDate || new Date(0))) continue;
    
    // If the current point is already above the target price, that's our crossing
    if (curr.value >= targetPrice) {
      return curr;
    }
    
    // Check if we cross the threshold between this point and the next
    if (curr.value < targetPrice && next.value >= targetPrice) {
      return next;
    }
  }
  
  return null; // No crossing found
};

export const renderPriceTimeRangePredictions = ({
  chartArea,
  predictions,
  activePredictions,
  xScale,
  yScale,
  theme,
  chartData = []
}: RenderProps): void => {
  // Create a group for price-time range predictions if it doesn't exist
  let priceTimeGroup = chartArea.select<SVGGElement>('.pricetime-predictions-group');
  if (priceTimeGroup.empty()) {
    priceTimeGroup = chartArea.append('g')
      .attr('class', 'pricetime-predictions-group');
  }
  
  // Filter and process price-time range predictions with crossing points
  const priceTimeData = predictions.filter(p => 
    p.prediction_type === 'pricetimerange' && 
    p.start_date && p.end_date && 
    p.price_min != null
  ).map(p => {
    // Find crossing point for this prediction
    const crossingPoint = findCrossingPoint(chartData, p);
    
    return {
      ...p,
      start_date: typeof p.start_date === 'string' ? new Date(p.start_date!) : p.start_date,
      end_date: typeof p.end_date === 'string' ? new Date(p.end_date!) : p.end_date,
      crossingPoint
    };
  });
  
  // Add debug logging
  console.log('Price time range crossings:', priceTimeData.filter(p => p.crossingPoint).length);
  
  // Create a unique key function for data joining
  const keyFn = (d: any) => `pricetime-${d.id}`;
  
  // Main horizontal lines
  const priceTimeLines = priceTimeGroup.selectAll<SVGLineElement, Prediction>('.pricetime-line')
    .data(priceTimeData, keyFn);
  
  // Enter new lines
  const enterLines = priceTimeLines.enter()
    .append('line')
    .attr('class', 'pricetime-line')
    .attr('data-id', d => d.id)
    .attr('stroke-width', 2);
  
  // Update all lines (both new and existing)
  const allLines = enterLines.merge(priceTimeLines as any)
    .attr('x1', d => xScale(d.start_date!))
    .attr('x2', d => xScale(d.end_date!))
    .attr('y1', d => yScale(Number(d.price_min)))
    .attr('y2', d => yScale(Number(d.price_min)))
    .attr('stroke', d => {
      if (d.success_status === 'success') return 'rgba(76, 175, 80, 0.9)'; // Success green
      if (d.success_status === 'miss') return 'rgba(244, 67, 54, 0.9)'; // Failed red (changed from 'failed')
      return 'rgba(255, 152, 0, 0.9)'; // Pending orange
    })
    .attr('stroke-dasharray', '4,3') // Clear dashed line
    .attr('stroke-linecap', 'round'); // Rounded ends for nicer appearance
  
  // Set visibility based on active state
  allLines.classed('prediction-active', d => activePredictions.includes(d.id))
    .classed('prediction-inactive', d => !activePredictions.includes(d.id));
  
  // Remove old lines
  priceTimeLines.exit().remove();
  
  // Add crossing point markers - ensure we're only using data with valid crossing points
  const crossingPoints = priceTimeGroup.selectAll<SVGCircleElement, any>('.crossing-marker')
    .data(priceTimeData.filter(p => p.crossingPoint !== null), keyFn);
  
  // Enter new crossing markers
  const enterCrossingPoints = crossingPoints.enter()
    .append('circle')
    .attr('class', 'crossing-marker')
    .attr('data-id', d => d.id)
    .attr('r', 6)
    .attr('stroke', 'white')
    .attr('stroke-width', 2);
  
  // Update all crossing markers - now TypeScript knows crossingPoint isn't null
  const allCrossingPoints = enterCrossingPoints.merge(crossingPoints as any)
    .attr('cx', d => xScale(d.crossingPoint!.time))
    .attr('cy', d => yScale(d.crossingPoint!.value))
    .attr('fill', 'rgba(76, 175, 80, 1)'); // Success green
  
  // Set visibility based on active state
  allCrossingPoints.classed('prediction-active', d => activePredictions.includes(d.id))
    .classed('prediction-inactive', d => !activePredictions.includes(d.id));
  
  // Add tooltips for crossing points
  allCrossingPoints.attr('title', d => {
    const name = d.influencer?.name || 'Expert';
    const date = d.crossingPoint!.time.toLocaleDateString();
    return `${name}'s prediction of ${formatPrice(Number(d.price_min))} was reached on ${date} at ${formatPrice(d.crossingPoint!.value)}`;
  });
  
  // Remove old crossing markers
  crossingPoints.exit().remove();
  
  // Add special crossing date labels - use same filter to ensure crossingPoint isn't null
  const crossingLabels = priceTimeGroup.selectAll<SVGTextElement, any>('.crossing-label')
    .data(priceTimeData.filter(p => p.crossingPoint !== null), keyFn);
    
  // Enter new crossing labels  
  const enterCrossingLabels = crossingLabels.enter()
    .append('text')
    .attr('class', 'crossing-label')
    .attr('data-id', d => d.id)
    .attr('font-size', '12px')
    .attr('text-anchor', 'middle')
    .attr('font-weight', 'bold');
    
  // Update all crossing labels - using non-null assertion as we've filtered the data
  const allCrossingLabels = enterCrossingLabels.merge(crossingLabels as any)
    .attr('x', d => xScale(d.crossingPoint!.time))
    .attr('y', d => yScale(d.crossingPoint!.value) - 15)
    .attr('fill', 'rgba(76, 175, 80, 1)')
    .text(d => {
      const date = d.crossingPoint!.time.toLocaleDateString(undefined, { 
        month: 'short', 
        day: 'numeric'
      });
      return `✓ ${date}`;
    });
    
  // Set visibility based on active state  
  allCrossingLabels.classed('prediction-active', d => activePredictions.includes(d.id))
    .classed('prediction-inactive', d => !activePredictions.includes(d.id));
    
  // Remove old crossing labels
  crossingLabels.exit().remove();
  
  // Add start markers (vertical lines) at the beginning of each price-time range
  const startMarkers = priceTimeGroup.selectAll<SVGLineElement, Prediction>('.pricetime-start-marker')
    .data(priceTimeData, keyFn);
  
  // Enter new start markers
  const enterStartMarkers = startMarkers.enter()
    .append('line')
    .attr('class', 'pricetime-start-marker')
    .attr('data-id', d => d.id)
    .attr('stroke-width', 1.5); // Slightly thinner than main line
  
  // Update all start markers
  const allStartMarkers = enterStartMarkers.merge(startMarkers as any)
    .attr('x1', d => xScale(d.start_date!))
    .attr('x2', d => xScale(d.start_date!))
    .attr('y1', d => yScale(Number(d.price_min)) - 10) // 10px up
    .attr('y2', d => yScale(Number(d.price_min)) + 10) // 10px down
    .attr('stroke', d => {
      if (d.success_status === 'success') return 'rgba(76, 175, 80, 1)'; 
      if (d.success_status === 'miss') return 'rgba(244, 67, 54, 1)'; // changed from 'failed'
      return 'rgba(255, 152, 0, 1)';
    });
  
  // Set visibility based on active state
  allStartMarkers.classed('prediction-active', d => activePredictions.includes(d.id))
    .classed('prediction-inactive', d => !activePredictions.includes(d.id));
  
  // Remove old start markers
  startMarkers.exit().remove();
  
  // Add end markers (vertical lines) at the end of each price-time range
  const endMarkers = priceTimeGroup.selectAll<SVGLineElement, Prediction>('.pricetime-end-marker')
    .data(priceTimeData, keyFn);
  
  // Enter new end markers
  const enterEndMarkers = endMarkers.enter()
    .append('line')
    .attr('class', 'pricetime-end-marker')
    .attr('data-id', d => d.id)
    .attr('stroke-width', 1.5);
  
  // Update all end markers
  const allEndMarkers = enterEndMarkers.merge(endMarkers as any)
    .attr('x1', d => xScale(d.end_date!))
    .attr('x2', d => xScale(d.end_date!))
    .attr('y1', d => yScale(Number(d.price_min)) - 10)
    .attr('y2', d => yScale(Number(d.price_min)) + 10)
    .attr('stroke', d => {
      if (d.success_status === 'success') return 'rgba(76, 175, 80, 1)'; 
      if (d.success_status === 'miss') return 'rgba(244, 67, 54, 1)'; // changed from 'failed'
      return 'rgba(255, 152, 0, 1)';
    });
  
  // Set visibility based on active state
  allEndMarkers.classed('prediction-active', d => activePredictions.includes(d.id))
    .classed('prediction-inactive', d => !activePredictions.includes(d.id));
  
  // Remove old end markers
  endMarkers.exit().remove();
  
  // Add price-time range labels
  const labels = priceTimeGroup.selectAll<SVGTextElement, Prediction>('.pricetime-label')
    .data(priceTimeData, keyFn);
  
  // Enter new labels
  const enterLabels = labels.enter()
    .append('text')
    .attr('class', 'pricetime-label')
    .attr('data-id', d => d.id)
    .attr('font-size', '12px')
    .attr('text-anchor', 'start');
  
  // Update all labels with smart positioning for low prices
  const allLabels = enterLabels.merge(labels as any)
    .attr('x', d => xScale(d.end_date!) + 10)
    .attr('y', d => {
      const price = Number(d.price_min);
      const normalY = yScale(price) - 5;
      
      // General solution: For very low prices, position label at fixed position in upper chart area
      const isLowPrice = price < 30000; // Below $30k is considered low
      if (isLowPrice) {
        const [topPixel, bottomPixel] = yScale.range(); // Get actual pixel range of chart
        const upperAreaY = topPixel + 50; // Position 50 pixels from top of chart
        return upperAreaY;
      }
      
      return normalY;
    })
    .attr('fill', theme.text)
    .text(''); // Label removed - using black-orange label from main renderer instead
  
  // Set visibility based on active state
  allLabels.classed('prediction-active', d => activePredictions.includes(d.id))
    .classed('prediction-inactive', d => !activePredictions.includes(d.id));
  
  // Remove old labels
  labels.exit().remove();
};
